import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router";
import { useDialogs, useNotifications } from "@toolpad/core";
import { useApiClient } from "../../ApiClientProvider";
import { IEventDay } from "../../interfaces/IEventDay";
import { ILocationList } from "../../interfaces/ILocationList";
import { PublicEventService } from "../../services/publicEventService";
import { IDelegateRole } from "../../interfaces/IDelegateRole";
import { IDelegate } from "../../interfaces/IDelegate";
import { IEventSessionType } from "../../interfaces/IEventSessionType";
import useMediaQuery from "@mui/material/useMediaQuery";
import RegisteredUserSummary from "../../components/Events/Shared/RegisteredUserSummary";
import PendingApprovalSummary from "../../components/Events/Shared/PendingApprovalSummary";
import { paths, buildPath } from "../../routes/paths";
import config from "../../config";
import { APIProvider } from "@vis.gl/react-google-maps";
import { Box, Container, Grid, Tabs, Tab, useTheme, Button, Typography, CircularProgress, alpha, Alert, Paper, Stack, DialogContent, DialogActions, TextField } from "@mui/material";
import ParticipantDialogFrame from '../../components/Dialogs/ParticipantDialogFrame';
import { Favorite, FavoriteBorder, Lock } from "@mui/icons-material";
import dayjs from "dayjs";
import EventBanner from "../../components/Events/Shared/EventBanner";
import EventLocations from "../../components/Events/Shared/EventLocations";
import EventOverview from "../../components/Events/Shared/EventOverview";
import EventSchedule from "../../components/Events/Shared/EventSchedule";
import EventSidebar from "../../components/Events/Shared/EventSidebar";
import EventDelegates from "../../components/Events/Shared/EventDelegates";
import EventAttendees from "../../components/Events/Shared/EventAttendees";
import { AttendeeVisibility } from "../../enums/AttendeeVisibility";
import ProfileDialog from "../../components/Dialogs/ProfileDialog";
import { logEvent, shouldTrackEventView, getAnalyticsSessionId, getUtmParameters } from "../../utils/analytics";
import { EventRegistrationStatus } from "../../enums/EventRegistrationStatus";
import { useSession } from "../../SessionContext";
import { toSeoAbsoluteUrl } from "../../utils/seoSiteUrl";
import PendingTicketsSummary from "../../components/Events/Shared/PendingTicketsSummary";
import TicketsAvailableSummary from "../../components/Events/Shared/TicketsAvailableSummary";
import { IPublicEventDetails } from "../../interfaces/IPublicEventDetails";
import { IEventList } from "../../interfaces/IEventList";
import AnimatedGradient from "../../components/AnimatedGradient";
import { participantContentSx } from '../../components/ParticipantPage';
import ScrollReveal from "../../components/ScrollReveal";
import { ParticipantEventService } from "../../services/participantEventService";
import { IParticipantEventFile } from "../../interfaces/IParticipantEventFile";
import ParticipantFilesPanel from "../../components/Events/Shared/ParticipantFilesPanel";
import RelatedOrganiserEventsCarousel from "../../components/Events/Shared/RelatedOrganiserEventsCarousel";
import { IEventOrganiser } from "../../interfaces/IEventOrganiser";
import { OrganisationRow } from "../../components/entities/organisations/OrganisationRow";
import { IContactUsRequest } from "../../interfaces/IContactUsRequest";
import NotFoundPage from "../NotFoundPage";
import { ISessionDelegate } from "../../interfaces/ISessionDelegate";
import PageMetadata from "../../components/SEO/PageMetadata";
import EventSchema from "../../components/SEO/EventSchema";
import { useEventSchemaMonitor } from "../../hooks/useEventSchemaMonitor";
import { toast } from "../../components/snackbar";
import RescheduleNotice from "../../components/Events/Shared/RescheduleNotice";


interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`event-tabpanel-${index}`}
            aria-labelledby={`event-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
        </div>
    );
}

export default function EventDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const notifications = useNotifications();
    const location = useLocation();
    // Organiser preview renders this page under the protected /manage preview route
    const isPreview = location.pathname.startsWith(paths.preview.root);
    const theme = useTheme();
    const { session } = useSession();
    const isLoggedIn = !!session;
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [event, setEvent] = useState<IPublicEventDetails | null>(null);
    const [locations, setLocations] = useState<ILocationList[]>([]);
    const [delegateRoles, setDelegateRoles] = useState<IDelegateRole[]>([]);
    const [delegates, setDelegates] = useState<IDelegate[]>([]);
    const [eventTypes, setEventTypes] = useState<IEventSessionType[]>([]);
    const [loading, setLoading] = useState(true);
    const [scheduleLoading, setScheduleLoading] = useState(false);
    const [delegatesLoading, setDelegatesLoading] = useState(false);
    const [hasLoadedScheduleData, setHasLoadedScheduleData] = useState(false);
    const [hasLoadedDelegatesData, setHasLoadedDelegatesData] = useState(false);
    const [isPrivateEvent, setIsPrivateEvent] = useState(false);
    const [expandedRoom, setExpandedRoom] = useState<string>("");
    const [selectedDay, setSelectedDay] = useState(0);
    const [imageUrl, setImageUrl] = useState<string | null>(null);
    const [resolvedProtectedImage, setResolvedProtectedImage] = useState<{ source: string; url: string } | null>(null);
    const [expandedLocation, setExpandedLocation] = useState<string | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);
    const [selectedTab, setSelectedTab] = useState(0);
    const [relatedEvents, setRelatedEvents] = useState<IEventList[]>([]);
    const [sharedFiles, setSharedFiles] = useState<IParticipantEventFile[]>([]);
    const [organiser, setOrganiser] = useState<IEventOrganiser | null>(null);
    const [isOrganiserFollowed, setIsOrganiserFollowed] = useState(false);
    const [isFollowSubmitting, setIsFollowSubmitting] = useState(false);
    const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
    const [isContactSubmitting, setIsContactSubmitting] = useState(false);
    const [contactError, setContactError] = useState<string | null>(null);
    const [rescheduleNoticeDismissed, setRescheduleNoticeDismissed] = useState(false);
    const [contactForm, setContactForm] = useState<IContactUsRequest>({
        YourName: session?.user?.name || "",
        EmailAddress: session?.user?.email || "",
        Subject: "",
        Message: ""
    });
    const dialogs = useDialogs();
    const apiClient = useApiClient("applicationClient");
    let endpoint = "event";
    if (location.pathname.startsWith("/events/web")) {
        endpoint = "events/web";
    }
    const eventService = useMemo(() => new PublicEventService(apiClient, endpoint, isPreview), [apiClient, endpoint, isPreview]);
    const participantEventService = useMemo(() => new ParticipantEventService(apiClient), [apiClient]);

    // Monitor Event JSON-LD injection (development only)
    useEventSchemaMonitor(id || '', !loading && !!event);

    // Each schedule change has its own dismissal so a fresh reschedule shows a fresh notice
    useEffect(() => {
        const changeId = event?.latestScheduleChange?.id;
        if (!changeId) return;
        setRescheduleNoticeDismissed(localStorage.getItem(`reschedule-notice-${changeId}`) === 'dismissed');
    }, [event?.latestScheduleChange?.id]);

    const normalizedImageUrl = typeof imageUrl === 'string' ? imageUrl.trim() : '';
    const isProtectedImageRoute = /\/api\/application\/event\/[^/]+\/image(?:\?.*)?$/.test(normalizedImageUrl);
    const displayImageUrl = isProtectedImageRoute
        ? (resolvedProtectedImage?.source === normalizedImageUrl ? resolvedProtectedImage.url : null)
        : (normalizedImageUrl || null);

    const isRegistrationOpen = (deadline: string): boolean => {
        return dayjs(deadline).endOf('day').isAfter(dayjs());
    };

    const daysUntilDeadline = (deadline: string): number => {
        return Math.max(0, dayjs(deadline).startOf("day").diff(dayjs().startOf("day"), "day") + 1);
    };

    const fetchEventShell = useCallback(async () => {
        if (id && id !== "new") {
            const searchParams = new URLSearchParams(location.search);
            const isInvite = searchParams.get('invite') === 'true';

            // If the user arrived via an invite link but isn't signed in, send them to sign in first
            // so their account can be validated against the invite on return
            if (isInvite && !isLoggedIn) {
                const returnUrl = `${location.pathname}${location.search}`;
                navigate(`${paths.signIn}?returnUrl=${encodeURIComponent(returnUrl)}`);
                return;
            }

            try {
                const [data, fallbackImageUrl] = await Promise.all([
                    eventService.get(id),
                    eventService.getImage(id).catch(() => null),
                ]);

                setEvent({ ...data, eventDays: [] });
                setLocations([]);
                setEventTypes([]);
                setDelegates([]);
                setDelegateRoles([]);
                setRelatedEvents([]);
                setSharedFiles([]);
                setOrganiser(null);
                setIsOrganiserFollowed(false);
                setHasLoadedScheduleData(false);
                setHasLoadedDelegatesData(false);
                setExpandedLocation(null);
                setIsInitialized(false);
                setSelectedDay(0);
                setImageUrl(data.imageUrl ?? fallbackImageUrl);
            } catch (error: unknown) {
                const requestError = error as { status?: number };

                if (requestError.status === 403) {
                    setIsPrivateEvent(true);
                    return;
                }
                throw error;
            }
        }
    }, [eventService, id, isLoggedIn, location.pathname, location.search, navigate]);

    const loadScheduleData = useCallback(async () => {
        if (!id || id === "new" || hasLoadedScheduleData || scheduleLoading) {
            return;
        }

        setScheduleLoading(true);
        try {
            const [locationData, eventTypeData, eventDays] = await Promise.all([
                eventService.getLocations(id),
                eventService.getTypes(id),
                eventService.getDays(id),
            ]);

            const eventDaysWithSessions = await Promise.all(
                eventDays.map(async (day) => ({
                    ...day,
                    eventSessions: await eventService.getSessions(id, day.id ?? ""),
                }))
            );

            setLocations(locationData);
            setEventTypes(eventTypeData);
            setEvent(prev => prev ? { ...prev, eventDays: eventDaysWithSessions } : prev);
            setHasLoadedScheduleData(true);
        } catch {
            notifications.show("Error loading event schedule", {
                severity: "error",
                autoHideDuration: 3000,
            });
        } finally {
            setScheduleLoading(false);
        }
    }, [eventService, hasLoadedScheduleData, id, notifications, scheduleLoading]);

    const loadDelegatesData = useCallback(async () => {
        if (!id || id === "new" || hasLoadedDelegatesData || delegatesLoading) {
            return;
        }

        setDelegatesLoading(true);
        try {
            let eventDays = event?.eventDays ?? [];

            if (eventDays.length === 0) {
                eventDays = await eventService.getDays(id);
            }

            const daysWithSessions = await Promise.all(
                eventDays.map(async (day) => {
                    const sessions = day.eventSessions?.length
                        ? day.eventSessions
                        : await eventService.getSessions(id, day.id ?? "");

                    const sessionsWithDelegates = await Promise.all(
                        sessions.map(async (session) => ({
                            ...session,
                            sessionDelegates: await eventService.getSessionDelegates(
                                id,
                                day.id ?? "",
                                session.id ?? ""
                            ),
                        }))
                    );

                    return {
                        ...day,
                        eventSessions: sessionsWithDelegates,
                    };
                })
            );

            const [delegateData, delegateRoleData] = await Promise.all([
                eventService.getDelegates(id),
                eventService.getDelegateRoles(id),
            ]);

            setDelegates(delegateData);
            setDelegateRoles(delegateRoleData);
            setEvent(prev => prev ? { ...prev, eventDays: daysWithSessions } : prev);
            setHasLoadedDelegatesData(true);
        } catch {
            notifications.show("Error loading event delegates", {
                severity: "error",
                autoHideDuration: 3000,
            });
        } finally {
            setDelegatesLoading(false);
        }
    }, [delegatesLoading, event, eventService, hasLoadedDelegatesData, id, notifications]);

    function getAllSessionDelegates(event: IPublicEventDetails): ISessionDelegate[] {
        if (!event?.eventDays) return [];
        return event.eventDays.flatMap(day =>
            day.eventSessions?.flatMap(session =>
                session.sessionDelegates ?? []
            ) ?? []
        );
    }

    function isAttendeesVisible(event: IPublicEventDetails): boolean {
        switch (event.attendeeVisibility) {
            case AttendeeVisibility.Never:
                return false;
            case AttendeeVisibility.OnCreation:
                return true;
            case AttendeeVisibility.WhenLive: {
                const now = dayjs();
                return now.isAfter(dayjs(event.startDate)) && now.isBefore(dayjs(event.endDate));
            }
            case AttendeeVisibility.AfterFinished:
                return dayjs().isAfter(dayjs(event.endDate));
            default:
                return false;
        }
    }

    const groupEventDaysByDate = (
        eventDays: IEventDay[],
        locations: ILocationList[],
        eventStartDate?: string
    ) => {
        const groupedDays: Record<
            string,
            {
                id: string;
                date: string;
                location: ILocationList | undefined;
                sessions: IEventDay["eventSessions"];
                isTBD?: boolean;
            }[]
        > = {};

        // Check if ALL event days are on the same date (single day event)
        // const eventStartDateOnly = eventStartDate ? String(eventStartDate).split("T")[0] : "";
        const allEventDaysHaveSameDate = eventDays.length > 0 && eventDays.every((day) => {
            const dayDateOnly = day.date ? String(day.date).split("T")[0] : "";
            return dayDateOnly === String(eventDays[0].date).split("T")[0];
        });


        eventDays.forEach((day, idx) => {
            const location = locations.find((loc) => loc.id === day.locationId);
            // For single-day events (all days same date), use eventStartDate if available to avoid timezone issues
            // This is a workaround for backend returning eventDays with incorrect dates
            const sourceDate = (allEventDaysHaveSameDate && eventStartDate) ? eventStartDate : day.date;
            const dayDateOnly = sourceDate ? String(sourceDate).split("T")[0] : "";
            // Parse with explicit format to avoid timezone conversion
            const formattedDate = dayjs(dayDateOnly, "YYYY-MM-DD").format("MMM D, YYYY");



            if (!groupedDays[formattedDate]) {
                groupedDays[formattedDate] = [];
            }

            groupedDays[formattedDate].push({
                id: day.id || "",
                date: formattedDate,
                location,
                sessions: day.eventSessions,
                isTBD: day.isTBD,
            });
        });

        return groupedDays;
    };

    const groupedDays = useMemo(
        () => event
            ? groupEventDaysByDate(event.eventDays, locations, event.startDate)
            : {},
        [event, locations]
    );
    //console.log('groupedDays---->>>', groupedDays)


    const handleToggleFavourite = async () => {
        if (!event || !id) return;

        if (!isLoggedIn) {
            notifications.show("Please sign in to add favourites", {
                severity: "info",
                autoHideDuration: 3000
            });
            return;
        }

        try {
            logEvent('Event Details Page', 'click',
                event.isFavourite ? 'Remove Favourite' : 'Add Favourite');

            if (event.isFavourite) {
                await eventService.removeFromFavourites(id);
                setEvent({ ...event, isFavourite: false });
                notifications.show("Removed from favourites", {
                    severity: "success",
                    autoHideDuration: 2000
                });
            } else {
                await eventService.addToFavourites(id);
                setEvent({ ...event, isFavourite: true });
                notifications.show("Added to favourites", {
                    severity: "success",
                    autoHideDuration: 2000
                });
            }
        } catch {
            notifications.show("Failed to update favourite", {
                severity: "error",
                autoHideDuration: 3000
            });
        }
    };

    const onViewMyTickets = () => {
        logEvent('Event Details Page', 'click', 'View My Tickets');
        navigate(paths.public.tickets);
    };


    const onBuyMoreTicket = () => {
        logEvent('Event Details Page', 'click', 'Buy More Tickets');
        if (!event?.id) return;
        const url = buildPath(paths.public.events.addTickets, { id: event.id });
        navigate(url, { replace: false });
    };

    const onAddToCalendar = async () => {
        logEvent('Event Details Page', 'click', 'Add to Calendar');
        if (!event?.id) return;

        try {
            const { blob, fileName } = await eventService.downloadCalendar(event.id);
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = fileName || `${event.title.replace(/\s+/g, "_")}.ics`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            notifications.show("Unable to download calendar", {
                severity: "error",
                autoHideDuration: 3000
            });
        }
    };

    const onGetDirections = (locationId: string) => {

        const location = locations.find(loc => loc.id === locationId);
        const locationName = location?.name || 'Unknown Location';
        logEvent('Event Details Page', 'click', `Get Directions - ${locationName}`);
        if (!location) {
            notifications.show("Location not found", { autoHideDuration: 3000 });
            return;
        }
        const address = [
            location.name,
            location.streetAddress,
            location.city,
            location.region,
            location.postalCode // optional
        ].filter(Boolean).join(', ');
        const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
        window.open(url, '_blank');
    };

    const fetchSharedFiles = useCallback(async () => {
        if (!id || id === 'new' || !isLoggedIn) {
            setSharedFiles([]);
            return;
        }

        try {
            const files = await participantEventService.getSharedFiles(id);
            setSharedFiles(files ?? []);
        } catch {
            setSharedFiles([]);
        }
    }, [id, isLoggedIn, participantEventService]);

    const fetchRelatedEvents = useCallback(async () => {
        if (!id || id === 'new') {
            setRelatedEvents([]);
            return;
        }

        try {
            const related = await eventService.getRelatedEventsByOrganiser(id, 8);
            setRelatedEvents(Array.isArray(related) ? related : []);
        } catch {
            // Keep the event details page resilient if this optional endpoint is unavailable.
            setRelatedEvents([]);
        }
    }, [eventService, id]);

    const fetchOrganiser = useCallback(async () => {
        if (!id || id === 'new') {
            setOrganiser(null);
            setIsOrganiserFollowed(false);
            return;
        }

        try {
            const organiserData = await eventService.getOrganiser(id);
            setOrganiser(organiserData);

            if (isLoggedIn && organiserData?.companyId) {
                const followStates = await eventService.getFollowStates([organiserData.companyId]);
                setIsOrganiserFollowed(Boolean(followStates[organiserData.companyId]));
            } else {
                setIsOrganiserFollowed(false);
            }
        } catch {
            setOrganiser(null);
            setIsOrganiserFollowed(false);
        }
    }, [eventService, id, isLoggedIn]);

    const openContactDialog = () => {
        setContactForm({
            YourName: session?.user?.name || "",
            EmailAddress: session?.user?.email || "",
            Subject: organiser ? `Question about ${event?.title ?? "this event"}` : "",
            Message: ""
        });
        setIsContactDialogOpen(true);
    };

    const openOrganiserProfile = () => {
        if (!organiser) {
            return;
        }

        const companyKey = organiser.companyKey || organiser.companyId;
        navigate(buildPath(paths.public.eventOrganizerProfile, { companyKey }));
    };

    const redirectToSignIn = () => {
        const returnUrl = `${location.pathname}${location.search}`;
        navigate(`${paths.signIn}?returnUrl=${encodeURIComponent(returnUrl)}`);
    };

    const handleToggleOrganiserFollow = async () => {
        if (!organiser?.companyId || isFollowSubmitting) {
            return;
        }

        if (!isLoggedIn) {
            redirectToSignIn();
            return;
        }

        try {
            setIsFollowSubmitting(true);
            if (isOrganiserFollowed) {
                await eventService.unfollowOrganiser(organiser.companyId);
                setIsOrganiserFollowed(false);
                notifications.show("Unfollowed organiser", {
                    severity: "success",
                    autoHideDuration: 2000,
                });
            } else {
                await eventService.followOrganiser(organiser.companyId, 'EventDetails');
                setIsOrganiserFollowed(true);
                notifications.show("Now following organiser", {
                    severity: "success",
                    autoHideDuration: 2000,
                });
            }
        } catch {
            notifications.show("Failed to update follow status", {
                severity: "error",
                autoHideDuration: 3000,
            });
        } finally {
            setIsFollowSubmitting(false);
        }
    };

    const handleContactOrganiser = async () => {
        if (isContactSubmitting) return;
        if (!id || !contactForm.YourName.trim() || !contactForm.EmailAddress.trim() || !contactForm.Subject.trim() || !contactForm.Message.trim()) {
            setContactError('Complete all fields before sending your message.');
            return;
        }

        try {
            setIsContactSubmitting(true);
            setContactError(null);
            const response = await eventService.contactOrganiser(id, contactForm);

            if (!response.isSuccess) {
                setContactError(response.message || 'Your message could not be sent. Please try again.');
                return;
            }

            setIsContactDialogOpen(false);
            toast.success(response.message || 'Message sent to organiser');
        } catch {
            setContactError('Your message could not be sent. Please try again.');
        } finally {
            setIsContactSubmitting(false);
        }
    };

    const handleDownloadSharedFile = async (file: IParticipantEventFile) => {
        if (!id) return;

        const url = await participantEventService.getSharedFileUrl(id, file.id);
        if (!url) {
            notifications.show("This file is no longer available", { severity: "warning", autoHideDuration: 3000 });
            return;
        }

        const link = document.createElement('a');
        link.href = url;
        link.download = `${file.name}.${file.type}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleDelegateClick = (delegate: IDelegate, roleId?: string) => {
        if (!delegate?.id || !id) return;
        dialogs.open(ProfileDialog, { userId: delegate.id, eventId: id, roleId });
    };

    const handleToggleRelatedFavourite = async (eventId: string) => {
        if (!isLoggedIn) {
            notifications.show("Please sign in to add favourites", {
                severity: "info",
                autoHideDuration: 3000
            });
            return;
        }

        const targetEvent = relatedEvents.find(e => e.id === eventId);
        if (!targetEvent) {
            return;
        }

        try {
            if (targetEvent.isFavourite) {
                await eventService.removeFromFavourites(eventId);
                notifications.show("Removed from favourites", {
                    severity: "success",
                    autoHideDuration: 2000
                });
            } else {
                await eventService.addToFavourites(eventId);
                notifications.show("Added to favourites", {
                    severity: "success",
                    autoHideDuration: 2000
                });
            }

            setRelatedEvents(prev =>
                prev.map(item =>
                    item.id === eventId
                        ? { ...item, isFavourite: !item.isFavourite }
                        : item
                )
            );
        } catch {
            notifications.show("Failed to update favourite", {
                severity: "error",
                autoHideDuration: 3000
            });
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setIsPrivateEvent(false);

            try {
                await fetchEventShell();
            } catch {
                notifications.show("Error loading data", { autoHideDuration: 3000 });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [fetchEventShell, notifications]);

    useEffect(() => {
        if (!isProtectedImageRoute || !normalizedImageUrl) {
            return;
        }

        let isActive = true;
        let objectUrl: string | null = null;

        const resolveProtectedImage = async () => {
            try {
                const response = await apiClient.get<Blob>(normalizedImageUrl, { responseType: 'blob' });
                objectUrl = URL.createObjectURL(response.data);

                if (isActive) {
                    setResolvedProtectedImage({ source: normalizedImageUrl, url: objectUrl });
                }
            } catch {
                if (isActive) {
                    setResolvedProtectedImage(null);
                }
            }
        };

        void resolveProtectedImage();

        return () => {
            isActive = false;

            if (objectUrl) {
                URL.revokeObjectURL(objectUrl);
            }
        };
    }, [apiClient, isProtectedImageRoute, normalizedImageUrl]);

    // Check if event data needs refresh due to ticket removal
    useEffect(() => {
        if (!id) return;

        const refreshKey = `event-refresh-${id}`;
        const refreshTimestamp = sessionStorage.getItem(refreshKey);

        if (refreshTimestamp) {
            const timestamp = parseInt(refreshTimestamp, 10);
            const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

            // Only refresh if the flag was set within the last 5 minutes
            if (timestamp > fiveMinutesAgo) {
                sessionStorage.removeItem(refreshKey);
                fetchEventShell().catch(() => {
                    // Silently fail - initial data is already loaded
                });
            } else {
                // Clean up stale flag
                sessionStorage.removeItem(refreshKey);
            }
        }
    }, [id, location.pathname, fetchEventShell]);

    useEffect(() => {
        if (!event?.id) {
            return;
        }

        const loadOptionalData = async () => {
            await Promise.allSettled([
                fetchSharedFiles(),
                fetchRelatedEvents(),
                fetchOrganiser(),
            ]);
        };

        void loadOptionalData();
    }, [event?.id, fetchOrganiser, fetchRelatedEvents, fetchSharedFiles]);

    useEffect(() => {
        if (!event?.id) {
            return;
        }

        const loadVisibleTabData = async () => {
            if (selectedTab === 0 || selectedTab === 1) {
                await loadScheduleData();
                await loadDelegatesData();
            }
        };

        if (selectedTab === 0 || selectedTab === 1) {
            void loadVisibleTabData();
        }
    }, [event?.id, loadDelegatesData, loadScheduleData, selectedTab]);

    useEffect(() => {
        const trackView = () => {
            if (!event || !id) return;

            const userId = session?.user?.id;
            if (!shouldTrackEventView(userId)) return;

            const sessionId = userId ? undefined : getAnalyticsSessionId();
            const utm = getUtmParameters();

            eventService.trackEventView(
                id,
                sessionId,
                navigator.userAgent,
                document.referrer || undefined,
                'DirectLink',
                utm['utm_source'],
                utm['utm_medium'],
                utm['utm_campaign']
            ).catch(error => {
                console.warn('Failed to track event view:', error);
            });
        };

        trackView();
    }, [event, eventService, id, session?.user?.id]);

    useEffect(() => {
        if (!isInitialized && Object.keys(groupedDays).length > 0) {
            const firstDay = Object.values(groupedDays)[selectedDay] || [];
            queueMicrotask(() => {
                if (firstDay.length > 0) {
                    setExpandedLocation(firstDay[0].location?.id || firstDay[0].id || null);
                }
                setIsInitialized(true);
            });
        }
    }, [groupedDays, isInitialized, selectedDay]);

    const canonicalEventPath = id ? `/events/${encodeURIComponent(String(id))}` : '/events';

    if (loading) {
        return (
            <>
                <PageMetadata
                    title="Event Details | AttendIn"
                    description="Loading event details on AttendIn."
                    canonicalUrl={canonicalEventPath}
                />
                <Box
                    sx={{
                        minHeight: '100vh',
                        position: 'relative',
                        overflow: 'hidden',
                        bgcolor: 'background.default',
                    }}
                >
                    <AnimatedGradient
                        colors={['#493193', '#2fadd8', '#493193']}
                        duration={20}
                        opacity={0.1}
                    />
                    <Container>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '60vh',
                                py: 4,
                                gap: 3,
                                position: 'relative',
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress
                                size={60}
                                sx={{
                                    color: '#493193',
                                    '& .MuiCircularProgress-circle': {
                                        strokeLinecap: 'round',
                                    }
                                }}
                            />
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 500,
                                }}
                            >
                                Loading event details...
                            </Typography>
                        </Box>
                    </Container>
                </Box>
            </>
        );
    }

    if (isPrivateEvent) {
        return (
            <>
                <PageMetadata
                    title="Private Event | AttendIn"
                    description="This event is private and requires an invitation to access details."
                    canonicalUrl={canonicalEventPath}
                />
                <Container>
                    <Box sx={{ py: 8, textAlign: "center" }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                bgcolor: 'rgba(237, 108, 2, 0.1)',
                                mb: 3,
                            }}
                        >
                            <Lock sx={{ fontSize: 40, color: '#ed6c02' }} />
                        </Box>
                        <Typography
                            variant="h4"
                            sx={{
                                mb: 2,
                                fontWeight: 700,
                                color: 'text.primary',
                            }}
                        >
                            Private Event
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                mb: 4,
                                color: 'text.secondary',
                                maxWidth: 480,
                                mx: 'auto',
                            }}
                        >
                            This event is private. You need an invitation from the organiser to view it.
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={() => navigate("/")}
                            sx={{
                                bgcolor: '#493193',
                                color: 'white',
                                fontWeight: 600,
                                borderRadius: '50px',
                                px: 4,
                                py: 1.5,
                                textTransform: 'none',
                                boxShadow: '0 4px 14px rgba(73, 49, 147, 0.4)',
                                '&:hover': {
                                    bgcolor: '#3a2676',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 20px rgba(73, 49, 147, 0.5)',
                                },
                            }}
                        >
                            Browse Events
                        </Button>
                    </Box>
                </Container>
            </>
        );
    }

    if (!event) {
        return (
            <>
                <PageMetadata
                    title="Event Not Found | AttendIn"
                    description="The event you are looking for could not be found."
                    canonicalUrl={canonicalEventPath}
                />
                <NotFoundPage />
            </>
        );
    }



    const handleRegister = () => {
        if (isPreview) {
            notifications.show("Registration is disabled in preview.", { severity: "info", autoHideDuration: 3000 });
            return;
        }
        const currentPath = location.pathname;
        navigate(`${currentPath}/register`);
    };

    const handleDayChange = (
        event: React.SyntheticEvent,
        newValue: number
    ) => {
        setSelectedDay(newValue);
        logEvent('Event Details Page', 'click', `Schedule Day Filter - ${newValue}`);
        const firstDay = Object.values(groupedDays)[newValue];
        if (firstDay && firstDay.length > 0) {
            setExpandedLocation(firstDay[0].location?.id || firstDay[0].id || null);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setSelectedTab(newValue);
    };

    const registrationOpen = isRegistrationOpen(
        event.registrationDeadline.toString()
    );
    const daysRemaining = daysUntilDeadline(
        event.registrationDeadline.toString()
    );

    const handleLocationAccordionChange =
        (locationId: string | undefined) =>
            (event: React.SyntheticEvent, isExpanded: boolean) => {
                if (locationId) setExpandedLocation(isExpanded ? locationId : null);
            };

    return (
        <Box>
            <>
                <PageMetadata
                    title={`${event.title} | AttendIn`}
                    description={event.description.slice(0, 160)}
                    keywords={`${event.title}, ${event.categoryName || 'event'}, events, AttendIn, attend event`}
                    ogImage={event.imageUrl || imageUrl || undefined}
                    canonicalUrl={canonicalEventPath}
                />
                <EventSchema
                    event={{
                        id: event.id || id || '',
                        title: event.title,
                        description: event.description,
                        startDate: event.startUtc ?? event.startDate,
                        endDate: event.endUtc ?? event.endDate,
                        location: event.locations && event.locations.length > 0
                            ? {
                                name: event.locations[0].name,
                                address: event.locations[0].streetAddress,
                                city: event.locations[0].city,
                                postcode: event.locations[0].postalCode,
                                country: event.locations[0].country,
                            }
                            : undefined,
                        organiser: organiser
                            ? {
                                name: organiser.companyName,
                                url: toSeoAbsoluteUrl(`/organisers/${organiser.companyKey}`),
                            }
                            : undefined,
                        imageUrl: event.imageUrl || imageUrl || undefined,
                        eventUrl: toSeoAbsoluteUrl(`/events/${event.id || id}`),
                    }}
                />
            </>
            {isPreview && (
                <Alert
                    severity="info"
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => window.close()}
                        >
                            Close preview
                        </Button>
                    }
                    sx={{ borderRadius: 0, justifyContent: 'center' }}
                >
                    You're previewing as an attendee. Registration is disabled.
                </Alert>
            )}
            {/* Banner */}
            <EventBanner event={event} imageUrl={displayImageUrl} onToggleFavourite={handleToggleFavourite} />

            <Container maxWidth={isLoggedIn ? false : 'lg'} sx={theme => ({ ...(isLoggedIn ? participantContentSx(theme) : {}), mb: { xs: 4, md: 8 } })}>
                <APIProvider apiKey={config.googleMapsApiKey}>
                    <Grid container spacing={4}>
                        {/* Main Content */}
                        <Grid size={{ xs: 12, md: 8 }}>
                            <ScrollReveal>
                                <Box
                                    sx={{
                                        borderBottom: 1,
                                        borderColor: 'divider',
                                        mb: 2,
                                    }}
                                >
                                    <Tabs
                                        value={selectedTab}
                                        onChange={handleTabChange}
                                        variant={isMobile ? "scrollable" : "fullWidth"}
                                        scrollButtons={isMobile ? "auto" : false}
                                        sx={{
                                            '& .MuiTabs-indicator': {
                                                backgroundColor: '#493193',
                                                height: 3,
                                                borderRadius: '3px 3px 0 0',
                                            },
                                            '& .MuiTab-root': {
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                fontSize: '1rem',
                                                color: 'text.secondary',
                                                transition: 'all 0.3s ease',
                                                '&:hover': {
                                                    color: '#493193',
                                                    bgcolor: alpha('#493193', 0.05),
                                                },
                                                '&.Mui-selected': {
                                                    color: '#493193',
                                                },
                                            },
                                        }}
                                    >
                                        <Tab
                                            label="Event Details"
                                            id="event-tab-0"
                                            aria-controls="event-tabpanel-0"
                                        />
                                        <Tab
                                            label={`Delegates`}
                                            id="event-tab-1"
                                            aria-controls="event-tabpanel-1"
                                        />
                                        {event && isAttendeesVisible(event) && (
                                            <Tab
                                                label="Attendees"
                                                id="event-tab-2"
                                                aria-controls="event-tabpanel-2"
                                            />
                                        )}
                                    </Tabs>
                                </Box>
                            </ScrollReveal>
                            <TabPanel value={selectedTab} index={0}>
                                {event?.latestScheduleChange && !rescheduleNoticeDismissed && dayjs(event.startUtc ?? event.startDate).isAfter(dayjs()) && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <RescheduleNotice
                                            scheduleChange={event.latestScheduleChange}
                                            onDismiss={() => {
                                                localStorage.setItem(`reschedule-notice-${event.latestScheduleChange!.id}`, 'dismissed');
                                                setRescheduleNoticeDismissed(true);
                                            }}
                                            refundGuidance="If you hold a paid ticket and can't attend the new date, you can request a full refund from your tickets page."
                                        />
                                    </Box>
                                )}
                                {event?.registrationStatus === EventRegistrationStatus.RegistrationIncomplete && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <Alert
                                            severity="warning"
                                            sx={{
                                                borderRadius: 2,
                                                border: `1px solid ${alpha('#ed6c02', 0.3)}`,
                                            }}
                                        >
                                            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                                                Complete Your Registration
                                            </Typography>
                                            <Typography variant="body1" sx={{ mb: 2 }}>
                                                Your ticket has been accepted, but required registration details are still missing.
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                onClick={handleRegister}
                                                sx={{
                                                    borderRadius: '50px',
                                                    bgcolor: '#ed6c02',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    textTransform: 'none',
                                                    px: 3,
                                                    py: 1,
                                                    boxShadow: '0 4px 14px rgba(237, 108, 2, 0.4)',
                                                    '&:hover': {
                                                        bgcolor: '#e65100',
                                                        boxShadow: '0 6px 20px rgba(237, 108, 2, 0.5)',
                                                    },
                                                }}
                                            >
                                                Complete Registration
                                            </Button>
                                        </Alert>
                                    </Box>
                                )}
                                {event?.registrationStatus === EventRegistrationStatus.Registered && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <RegisteredUserSummary
                                            event={event}
                                            locations={locations}
                                            onAddToCalendar={onAddToCalendar}
                                            onBuyMoreTickets={onBuyMoreTicket}
                                            onGetDirections={onGetDirections}
                                            onViewMyTickets={onViewMyTickets}
                                        />
                                    </Box>
                                )}
                                {event?.registrationStatus === EventRegistrationStatus.Pending && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <PendingTicketsSummary
                                            event={event}
                                            locations={locations}
                                            onReviewTickets={onViewMyTickets}
                                            onAddToCalendar={onAddToCalendar}
                                            onBuyMoreTickets={onBuyMoreTicket}
                                            onGetDirections={onGetDirections}
                                            isPaymentPendingRegistration={event.isPaymentPendingRegistration}
                                        />
                                    </Box>
                                )}
                                {event?.registrationStatus === EventRegistrationStatus.PendingApproval && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <PendingApprovalSummary
                                            event={event}
                                            locations={locations}
                                            hasTickets={!event.isWaitlistRegistration}
                                        />
                                    </Box>
                                )}
                                {event?.registrationStatus === EventRegistrationStatus.TicketsAvailable && (
                                    <Box sx={{ mb: { xs: 3, md: 4 } }}>
                                        <TicketsAvailableSummary
                                            event={event}
                                            locations={locations}
                                            isWaitlistRegistration={event.isWaitlistRegistration}
                                            onRegister={handleRegister}
                                            onAssignTickets={onViewMyTickets}
                                            onAddToCalendar={onAddToCalendar}
                                            onBuyMoreTickets={onBuyMoreTicket}
                                            onGetDirections={onGetDirections}
                                        />
                                    </Box>
                                )}
                                {sharedFiles.length > 0 && (
                                    <ParticipantFilesPanel
                                        files={sharedFiles}
                                        onDownload={handleDownloadSharedFile}
                                    />
                                )}
                                {organiser && (
                                    <Paper
                                        elevation={1}
                                        sx={{
                                            p: { xs: 2, md: 3 },
                                            mb: { xs: 3, md: 4 },
                                            borderRadius: 3,
                                            border: `1px solid ${alpha('#493193', 0.12)}`,
                                        }}
                                    >
                                        <Typography
                                            variant="overline"
                                            sx={{
                                                color: 'text.secondary',
                                                letterSpacing: 1.2,
                                                fontWeight: 700,
                                            }}
                                        >
                                            Organised by
                                        </Typography>
                                        <Box sx={{ mt: 1 }}>
                                            <OrganisationRow
                                                organisation={{ ...organiser, eventCount: 0, communityCount: 0 }}
                                                onClick={openOrganiserProfile}
                                                subtitle="Event organiser"
                                                action={(
                                                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                                        <Button
                                                            variant={isOrganiserFollowed ? "contained" : "outlined"}
                                                            onClick={handleToggleOrganiserFollow}
                                                            disabled={isFollowSubmitting}
                                                            startIcon={isOrganiserFollowed ? <Favorite fontSize="small" /> : <FavoriteBorder fontSize="small" />}
                                                            sx={{
                                                                borderRadius: '999px',
                                                                minHeight: 38,
                                                                textTransform: 'none',
                                                                fontWeight: 700,
                                                                px: 2.5,
                                                                borderColor: alpha('#493193', 0.4),
                                                                color: isOrganiserFollowed ? 'white' : '#493193',
                                                                bgcolor: isOrganiserFollowed ? '#493193' : 'transparent',
                                                                '&:hover': {
                                                                    borderColor: '#493193',
                                                                    bgcolor: isOrganiserFollowed ? '#3a2676' : alpha('#493193', 0.05),
                                                                },
                                                            }}
                                                        >
                                                            {isOrganiserFollowed ? 'Following' : 'Follow'}
                                                        </Button>

                                                        <Button
                                                            variant="outlined"
                                                            onClick={openContactDialog}
                                                            aria-label="Contact organiser"
                                                            sx={{
                                                                borderRadius: '999px',
                                                                minHeight: 38,
                                                                textTransform: 'none',
                                                                fontWeight: 700,
                                                                px: 2.5,
                                                                borderColor: alpha('#493193', 0.4),
                                                                color: '#493193',
                                                                '&:hover': {
                                                                    borderColor: '#493193',
                                                                    bgcolor: alpha('#493193', 0.05),
                                                                },
                                                            }}
                                                        >
                                                            Contact
                                                        </Button>
                                                    </Stack>
                                                )}
                                            />
                                        </Box>
                                    </Paper>
                                )}
                                <EventOverview event={event} />
                                {scheduleLoading || delegatesLoading ? (
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: { xs: 2, md: 4 },
                                            mb: { xs: 3, md: 4 },
                                            borderRadius: 3,
                                            border: `1px solid ${alpha('#493193', 0.1)}`,
                                            boxShadow: `0 4px 20px ${alpha('#493193', 0.06)}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CircularProgress size={28} sx={{ color: '#493193' }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                    Loading schedule
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Sessions, locations, and delegate assignments are loading separately to keep the page responsive.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ) : hasLoadedScheduleData ? (
                                    <>
                                        <EventSchedule
                                            groupedDays={groupedDays}
                                            selectedDay={selectedDay}
                                            handleDayChange={handleDayChange}
                                            expandedLocation={expandedLocation}
                                            handleLocationAccordionChange={handleLocationAccordionChange}
                                            delegateRoles={delegateRoles}
                                            delegates={delegates}
                                            onDelegateClick={handleDelegateClick}
                                            eventTypes={eventTypes}
                                        />
                                        <EventLocations
                                            event={event}
                                            locations={locations}
                                            expandedRoom={expandedRoom}
                                            setExpandedRoom={setExpandedRoom}
                                        />
                                    </>
                                ) : null}
                            </TabPanel>
                            <TabPanel value={selectedTab} index={1}>
                                {delegatesLoading || scheduleLoading ? (
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: { xs: 2, md: 4 },
                                            borderRadius: 3,
                                            border: `1px solid ${alpha('#493193', 0.1)}`,
                                            boxShadow: `0 4px 20px ${alpha('#493193', 0.06)}`,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <CircularProgress size={28} sx={{ color: '#493193' }} />
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                                    Loading delegates
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    Delegate profiles and session assignments are loaded on demand for this tab.
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ) : hasLoadedDelegatesData ? (
                                    <EventDelegates
                                        sessionDelegates={getAllSessionDelegates(event)}
                                        delegates={delegates}
                                        delegateRoles={delegateRoles}
                                        onCardClick={handleDelegateClick}
                                    />
                                ) : null}
                            </TabPanel>
                            {event && isAttendeesVisible(event) && (
                                <TabPanel value={selectedTab} index={2}>
                                    <EventAttendees
                                        eventId={id}
                                        onCardClick={handleDelegateClick}
                                    />
                                </TabPanel>
                            )}
                        </Grid>

                        {/* Sidebar */}
                        <Grid size={{ xs: 12, md: 4 }}>
                            <EventSidebar
                                event={event}
                                locations={locations}
                                isLoggedIn={isLoggedIn}
                                registrationOpen={registrationOpen}
                                daysRemaining={daysRemaining}
                                handleRegister={handleRegister}
                                handleAssignTickets={onViewMyTickets}
                                handleBuyMoreTickets={onBuyMoreTicket}
                                handleReviewTickets={onViewMyTickets}
                            />
                        </Grid>
                    </Grid>

                    {relatedEvents.length > 0 && (
                        <RelatedOrganiserEventsCarousel
                            events={relatedEvents}
                            onToggleFavourite={handleToggleRelatedFavourite}
                        />
                    )}
                </APIProvider>
            </Container>

            <ParticipantDialogFrame
                open={isContactDialogOpen}
                onClose={() => !isContactSubmitting && setIsContactDialogOpen(false)}
                title="Contact organiser"
                busy={isContactSubmitting}
                fullWidth
                maxWidth="sm"
            >
                <DialogContent>
                    <Box component="form" id="contact-organiser-form" onSubmit={event => { event.preventDefault(); void handleContactOrganiser(); }}>
                    {contactError && <Alert severity="error" sx={{ mb: 2 }}>{contactError}</Alert>}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Your message will be sent to {organiser?.companyName ?? 'the organiser'} about {event?.title}.
                    </Typography>
                    <TextField
                        label="Your name"
                        disabled={isContactSubmitting}
                        required
                        fullWidth
                        margin="normal"
                        value={contactForm.YourName}
                        onChange={(e) => setContactForm(prev => ({ ...prev, YourName: e.target.value }))}
                    />
                    <TextField
                        label="Email address"
                        disabled={isContactSubmitting}
                        required
                        type="email"
                        fullWidth
                        margin="normal"
                        value={contactForm.EmailAddress}
                        onChange={(e) => setContactForm(prev => ({ ...prev, EmailAddress: e.target.value }))}
                    />
                    <TextField
                        label="Subject"
                        disabled={isContactSubmitting}
                        required
                        fullWidth
                        margin="normal"
                        value={contactForm.Subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, Subject: e.target.value }))}
                    />
                    <TextField
                        label="Message"
                        disabled={isContactSubmitting}
                        required
                        fullWidth
                        margin="normal"
                        multiline
                        minRows={4}
                        value={contactForm.Message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, Message: e.target.value }))}
                    />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setIsContactDialogOpen(false)}
                        disabled={isContactSubmitting}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        type="submit"
                        form="contact-organiser-form"
                        disabled={isContactSubmitting}
                        loading={isContactSubmitting}
                    >
                        Send message
                    </Button>
                </DialogActions>
            </ParticipantDialogFrame>
        </Box>
    );
}